import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { Controller, Path, PathValue } from 'react-hook-form';
import { BaseControlledInputProps } from '../basicControlledInputProps.types';
import FormFieldWrappers from '../FormFieldWrappers';
import { ReactNode } from 'react';
import { ExpandableGroup } from '../ExpandableGroup/ExpandableGroupComponent';
import {
  useFormTracking,
  useFormOptionTracking,
} from '../hooks/useFormTracking.hook';

export type CheckboxOption<VALUE> = {
  label: ReactNode;
  value: VALUE;
  disabled?: boolean;
};

type CheckboxGroupProps<VALUE> = {
  options: CheckboxOption<VALUE>[];
  accordionTitle?: string;
  defaultExpanded?: boolean;
  /** Number of option columns to lay the checkboxes out in. Defaults to 1. */
  columns?: number;
};

// Type helper to extract element type from array
type ArrayElement<T> = T extends (infer U)[] ? U : never;

export type ControlledCheckboxGroupProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
> = Omit<
  BaseControlledInputProps<FORM_TYPE, FIELD_NAME>,
  'onBlurCallback' | 'onFocusCallback' | 'ariaLabel' | 'ariaRole'
> &
  CheckboxGroupProps<ArrayElement<PathValue<FORM_TYPE, FIELD_NAME>>>;

export const ControlledCheckboxGroup = <
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
>({
  name,
  label,
  defaultValue = [] as PathValue<FORM_TYPE, FIELD_NAME>,
  disabled,
  hidden,
  control,
  required,
  options,
  columns = 1,
  trackingName,
  sensitive = true,
}: ControlledCheckboxGroupProps<FORM_TYPE, FIELD_NAME>) => {
  // Auto-generate tracking props if trackingName is provided
  const trackingProps = useFormTracking(trackingName, sensitive);
  const { generateOptionTracking } = useFormOptionTracking(
    trackingName,
    sensitive,
    options
  );

  return (
    <FormFieldWrappers.FieldWrapper>
      <FormFieldWrappers.InputWrapper>
        <Controller
          control={control}
          name={name}
          rules={{ required: required ? `${label} is required` : false }}
          defaultValue={defaultValue}
          render={({ field: { onChange, value } }) => {
            const currentValue = (value || []) as ArrayElement<
              PathValue<FORM_TYPE, FIELD_NAME>
            >[];

            const handleChange = (
              optionValue: ArrayElement<PathValue<FORM_TYPE, FIELD_NAME>>
            ) => {
              const newValue = currentValue.includes(optionValue)
                ? currentValue.filter(v => v !== optionValue)
                : [...currentValue, optionValue];
              onChange(newValue);
            };

            return (
              !hidden && (
                <ExpandableGroup title={label} {...trackingProps}>
                  <Box
                    sx={{
                      display: 'grid',
                      /*
                       * minmax(0, 1fr) rather than 1fr so a long option label
                       * cannot push a column past its share of the row.
                       */
                      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                    }}
                  >
                    {options.map(option => (
                      <FormControlLabel
                        key={String(option.value)}
                        control={
                          <Checkbox
                            {...generateOptionTracking(
                              option.value,
                              option.label as unknown as string
                            )}
                            checked={currentValue.includes(option.value)}
                            onChange={() => handleChange(option.value)}
                            disabled={disabled || option.disabled}
                          />
                        }
                        label={option.label}
                      />
                    ))}
                  </Box>
                </ExpandableGroup>
              )
            );
          }}
        />
      </FormFieldWrappers.InputWrapper>
    </FormFieldWrappers.FieldWrapper>
  );
};
